import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Gift } from 'lucide-react';
import { referralsApi } from '../api/referrals';
import { useAuthStore } from '../store/authStore';

interface ReferralCodeInputProps {
  variant?: 'card' | 'inline';
  onSuccess?: () => void;
}

export default function ReferralCodeInput({ variant = 'card', onSuccess }: ReferralCodeInputProps) {
  const { t } = useTranslation();
  const { fetchCurrentUser } = useAuthStore();
  const [referralCode, setReferralCode] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const handleSubmit = async () => {
    if (!referralCode.trim()) return;

    setSaving(true);
    setMessage(null);
    try {
      await referralsApi.setReferralCode(referralCode.trim().toUpperCase());
      await fetchCurrentUser();
      setMessage({ type: 'success', text: t('referral.codeApplied', 'Referral code applied! You\'ll get 1 month free on your first subscription.') });
      setReferralCode('');
      onSuccess?.();
    } catch (error: any) {
      const errorKey = error.response?.data?.message;
      let errorText: string;
      switch (errorKey) {
        case 'invalid_referral_code':
          errorText = t('referral.invalidCode', 'Invalid referral code. Please check and try again.');
          break;
        case 'cannot_use_own_code':
          errorText = t('referral.cannotUseOwnCode', 'You cannot use your own referral code.');
          break;
        case 'referral_code_already_set':
          errorText = t('referral.codeAlreadySet', 'A referral code has already been applied to your account.');
          break;
        case 'cannot_set_referral_after_subscription':
          errorText = t('referral.cannotSetAfterSubscription', 'Referral code cannot be applied after subscription.');
          break;
        default:
          errorText = t('referral.error', 'Failed to apply referral code');
      }
      setMessage({ type: 'error', text: errorText });
    } finally {
      setSaving(false);
    }
  };

  const containerClass = variant === 'card'
    ? 'bg-green-500/10 backdrop-blur-xl border border-green-500/30 rounded-2xl p-6'
    : 'backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl p-6';

  return (
    <div className={containerClass}>
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
          <Gift className="w-5 h-5 text-green-400" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-white">{t('referral.haveCode', 'Have a Referral Code?')}</h3>
          <p className="text-sm text-gray-400">{t('referral.bonus', 'Get 1 month FREE on your first subscription')}</p>
        </div>
      </div>

      {message && (
        <div className={`mb-4 p-3 rounded-lg ${message.type === 'success' ? 'bg-green-500/20 text-green-200' : 'bg-red-500/20 text-red-200'}`}>
          {message.text}
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3">
        <input
          type="text"
          value={referralCode}
          onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
          placeholder={t('referral.enterCode', 'Enter referral code')}
          className="flex-1 px-4 py-3 bg-white/5 border border-white/10 text-white rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none uppercase placeholder:normal-case"
          maxLength={20}
        />
        <button
          onClick={handleSubmit}
          disabled={saving || !referralCode.trim()}
          className="px-6 py-3 bg-green-600 hover:bg-green-500 text-white font-medium rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
        >
          {saving ? t('common.saving', 'Saving...') : t('referral.apply', 'Apply')}
        </button>
      </div>
    </div>
  );
}
